import { useState, useEffect, useCallback } from "react";
import { HandCoins, Banknote, Loader2, Send } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr, fmtDateTime } from "@/lib/format";
import { PageHeader, Loading, EmptyState, StatusBadge, StatCard } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Collections() {
  const [summary, setSummary] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([api.get("/deposits/summary"), api.get("/deposits")]);
      setSummary(s.data);
      setDeposits(d.data.items);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Loading />;

  return (
    <div>
      <PageHeader title="My Collections" subtitle="Money collected & deposits to owner" testid="collections-page">
        <Button onClick={() => setOpen(true)} data-testid="submit-deposit-btn" className="bg-bk-green hover:bg-bk-greenDark"><Send className="h-4 w-4 mr-1" /> Submit Deposit</Button>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 lg:gap-4">
        <StatCard testid="col-collected" label="Collected By Me" value={inr(summary.collected_by_me)} icon={HandCoins} tone="green" />
        <StatCard testid="col-deposited" label="Deposited" value={inr(summary.deposited)} icon={Banknote} tone="blue" />
        <StatCard testid="col-pending" label="Pending Deposit" value={inr(summary.pending)} icon={Banknote} tone="danger" />
      </div>

      <p className="font-display font-bold text-slate-700 mt-6 mb-3">Deposit History</p>
      {deposits.length === 0 ? <EmptyState icon={Banknote} title="No deposits yet" /> : (
        <div className="grid gap-2.5 lg:grid-cols-2">
          {deposits.map((d) => (
            <Card key={d.id} className="p-4" data-testid={`my-deposit-${d.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-800 tabular-nums">{inr(d.amount)}</p>
                  <p className="text-xs text-slate-400">{fmtDateTime(d.created_at)} · {d.method}</p>
                </div>
                <StatusBadge status={d.status} />
              </div>
              {d.status !== "pending_verification" && (
                <p className="text-xs text-slate-500 mt-2">Verified: {inr(d.deposited_amount)}{d.remaining > 0 && ` · Remaining ${inr(d.remaining)}`}</p>
              )}
              {d.reject_reason && <p className="text-xs text-bk-danger mt-1">Rejected: {d.reject_reason}</p>}
            </Card>
          ))}
        </div>
      )}

      <SubmitDialog open={open} onOpenChange={setOpen} pending={summary.pending} onDone={load} />
    </div>
  );
}

function SubmitDialog({ open, onOpenChange, pending, onDone }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) { setAmount(String(pending || "")); setMethod("cash"); setNote(""); setReason(""); } }, [open, pending]);

  const partial = Number(amount) < pending;

  const submit = async () => {
    if (amount === "" || Number(amount) < 0) { toast.error("Enter amount"); return; }
    setLoading(true);
    try {
      await api.post("/deposits", { amount: Number(amount), method, note, reason });
      toast.success("Deposit submitted for verification");
      onOpenChange(false); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">Submit Collection</DialogTitle></DialogHeader>
        <div className="text-sm text-slate-500 -mt-2">Pending to deposit: <span className="font-semibold text-bk-danger">{inr(pending)}</span></div>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Amount to deposit (₹)</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="deposit-amount" /></div>
          <div className="space-y-1.5"><Label>Method</Label>
            <Select value={method} onValueChange={setMethod}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="upi">UPI</SelectItem><SelectItem value="bank">Bank Transfer</SelectItem></SelectContent></Select>
          </div>
          {partial && (
            <div className="space-y-1.5"><Label>Reason for partial / non-deposit</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="deposit-reason"><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Patient payment pending">Patient payment pending</SelectItem>
                  <SelectItem value="Cash issue">Cash issue</SelectItem>
                  <SelectItem value="Will deposit later">Will deposit later</SelectItem>
                  <SelectItem value="Personal reason">Personal reason</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5"><Label>Note (optional)</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={loading} data-testid="deposit-submit" className="bg-bk-green hover:bg-bk-greenDark">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
